import { useEffect, useState, useMemo } from "react";
import store from "../../../Store/Store.js";
import styles from "./ProjectsList.module.css";
import { Pagination } from "antd";

const Projects = ({ openUpdate }) => {
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [updateMessageVisible, setUpdateMessageVisible] = useState(false);

  // Compute master toggle state based on actual data
  const allEnabled = useMemo(() => {
    if (data.length === 0) return false;
    return data.every((item) => item.isActive === true);
  }, [data]);

  const atLeastOneEnabled = useMemo(() => {
    return data.some((item) => item.isActive === true);
  }, [data]);

  // Master toggle should be ON only if ALL are enabled
  // (most common UX pattern for "ENABLE ALL")
  var masterToggleOn = allEnabled;

  // If you prefer "ON if at least one is enabled" → use this instead:
  var masterToggleOn = atLeastOneEnabled;

  let testData = {
    data: [
      {
        project_name: "Food",
        description:
          "sdghfkjlhsdflgkhkjdsfhgjkhdfkjlghskdfjhgkjdfhgjkhlfdkglksfdhgkjhfdkgjhkjhgeruytuhkjxb xcbz,vbrtorto",
        isActive: true,
      },
      {
        project_name: "Fuel",
        description: "Fuel costs for company vehicles or travel",
        isActive: true,
      },
      {
        project_name: "Office Supplies and IT",
        description: "Stationery, hardware, and IT consumables",
        isActive: true,
      },
      {
        project_name: "Travel",
        description: "General travel expenses excluding accommodation and fuel",
        isActive: true,
      },
      {
        project_name: "Materials/Supplies",
        description: "General materials and supplies for operations",
        isActive: false,
      },
      {
        project_name: "Gifts & Entertainment",
        description: "Client gifts and entertainment expenses",
        isActive: false,
      },
      {
        project_name: "Uncategorized",
        description: "Expenses not fitting into specific categories",
        isActive: true,
      },
      {
        project_name: "Other",
        description: "Miscellaneous expenses not covered elsewhere",
        isActive: true,
      },
      {
        project_name: "Repair",
        description: "Repairs to equipment or facilities",
        isActive: false,
      },
      {
        project_name: "Accommodation",
        description: "Lodging expenses for business travel or temporary stays",
        isActive: false,
      },
      {
        project_name: "Medical",
        description: "Medical expenses for employees or insurance",
        isActive: true,
      },
      {
        project_name: "System Charge",
        description: "Fees for system usage or subscriptions",
        isActive: true,
      },
      {
        project_name: "Materials Purchase for Project/Site",
        description: "Materials specifically for project or site work",
        isActive: true,
      },
      {
        project_name: "Subcontracting/Labor",
        description: "Payments to subcontractors or temporary labor",
        isActive: true,
      },
      {
        project_name: "Professional & Technical Services",
        description: "Fees for technical or professional consultants",
        isActive: true,
      },
      {
        project_name: "Legal Expenses & Charges",
        description: "Legal fees and related charges",
        isActive: true,
      },
      {
        project_name: "Rent / Utility",
        description: "Office rent and utility bills",
        isActive: true,
      },
    ],
    message: "Success",
    status: 200,
  };

  useEffect(() => {
    setData(testData.data);
  }, []);

  const handleMasterToggle = () => {
    setData((prev) =>
      prev.map((item) => ({
        ...item,
        isActive: !masterToggleOn, // flip to the opposite of current master state
      })),
    );
  };

  const handleSingleToggle = (projectName) => {
    setData((prev) =>
      prev.map((item) =>
        item.project_name === projectName
          ? { ...item, isActive: !item.isActive }
          : item,
      ),
    );
  };

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleSave = async () => {
    setUpdateMessageVisible(true);
    // await ProjectsUpdated();  ← call your real API here
    console.log("Saving projects:", data);

    await new Promise((r) => setTimeout(r, 2000));
    setUpdateMessageVisible(false);
  };

  const startIndex = (currentPage - 1) * pageSize;
  const currentPageData = data.slice(startIndex, startIndex + pageSize);

  return (
    <div className={styles.ProjectListContainer}>
      <div className={styles.ProjectListHead}>
        <div className={styles.ProjectListHeading}>Project List</div>

        <div className={styles.ProjectListBtnConatiner}>
          <div className={styles.ProjectListEnableDisable}>
            ENABLE ALL / DISABLE ALL
            <div
              className={styles.ProjectListPointer}
              onClick={handleMasterToggle}
            >
              <img
                src={masterToggleOn ? "/Track(Active).svg" : "/Track.svg"}
                className={styles.Disbtn}
                alt={masterToggleOn ? "All enabled" : "Not all enabled"}
              />
            </div>
          </div>

          {updateMessageVisible && (
            <div className={styles.AlignContainer}>
              <div className={styles.SuccessMSGButton}>
                <img
                  alt=""
                  src="/check-circle.svg"
                  className={styles.btn_img}
                />
                Projects have been updated.
              </div>
            </div>
          )}

          <div className={styles.ProjectListApplyBtn} onClick={handleSave}>
            Save Changes
          </div>
        </div>
      </div>

      <div className={styles.ProjectListBody}>
        <table className={styles.table}>
          <thead className={styles.tableHead}>
            <tr className={styles.tableHeaderRow}>
              <th className={styles.tableHeaderRowCell}>
                <div className={styles.head1}>NAME</div>
              </th>
              <th className={styles.tableHeaderRowCell}>
                <div className={styles.head}>DESCRIPTION</div>
              </th>
              <th className={styles.tableHeaderRowCell1}>
                <div className={styles.head}>ENABLE/DISABLE</div>
              </th>
              <th className={styles.tableHeaderRowCell}>
                <div className={styles.head}>ACTION</div>
              </th>
            </tr>
          </thead>

          {currentPageData.length > 0 ? (
            <tbody className={styles.tableHead}>
              {currentPageData.map((detail) => (
                <tr key={detail.project_name} className={styles.tableBodyrow}>
                  <td className={styles.tableBodydata2}>
                    <div className={styles.datacontainerContainer}>
                      {detail.project_name}
                    </div>
                  </td>
                  <td className={styles.tableBodydata}>
                    <div
                      className={`${styles.datacontainerContainer1} ${styles.truncatedCell}`}
                      title={
                        detail?.description?.length > 33
                          ? detail.description
                          : undefined
                      }
                    >
                      {detail?.description?.length > 33
                        ? detail.description.substring(0, 33) + "..."
                        : detail?.description || "-"}
                    </div>
                  </td>
                  <td className={styles.tableBodydata1}>
                    <div className={styles.button}>
                      {detail.project_name !== "Uncategorized" &&
                      detail.project_name !== "Other" &&
                      !store.isUserViewAdmin &&
                      !store.isUserGlobalViewAdmin ? (
                        <button
                          className={styles.button1}
                          onClick={() =>
                            handleSingleToggle(detail.project_name)
                          }
                        >
                          {detail.isActive ? (
                            <img
                              src="/Track(Active).svg"
                              className={styles.btn_img}
                              alt="Active"
                            />
                          ) : (
                            <img
                              src="/Track.svg"
                              className={styles.btn_img}
                              alt="Inactive"
                            />
                          )}
                        </button>
                      ) : (
                        <button className={styles.button22}>
                          {detail.isActive ? (
                            <img
                              src="/Track(Active).svg"
                              className={styles.btn_img22}
                              alt="Active"
                            />
                          ) : (
                            <img
                              src="/Track.svg"
                              className={styles.btn_img22}
                              alt="Inactive"
                            />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className={styles.tableBodydata}>
                    <button className={styles.button22} onClick={openUpdate}>
                      <img
                        src="/edit-02.svg"
                        className={styles.btn_img22}
                        alt="Edit"
                      />
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          ) : (
            <div className={styles.NoDataFound}>
              To begin, please add your first project.
            </div>
          )}
        </table>

        <div className={styles.bottom}>
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={data.length}
            onChange={handlePaginationChange}
            showSizeChanger
          />
        </div>
      </div>
    </div>
  );
};

export default Projects;
